import { useMemo, useState } from 'react';
import {
  flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type ColumnFiltersState, type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input } from './Input';
import { Skeleton } from './EmptyState';

/** Default rows shown per page, and the sizes the user can expand to. */
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  searchPlaceholder?: string;
  searchableKeys?: (keyof TData)[];
  toolbar?: React.ReactNode;
  emptyState?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Optional custom renderer for the mobile (md:hidden) card layout. If
   *  omitted, the table auto-derives a card from the first visible column
   *  (title) plus the next 2–3 columns (secondary lines). */
  mobileCardRender?: (row: TData) => React.ReactNode;
  /** Optional controlled search-text. When set, the parent owns the value
   *  (e.g. so saved views can drive it). Otherwise the table manages it
   *  internally as before. */
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  /** Optional controlled column-level filters (substring contains). Used
   *  by ListView's Filter popover. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (next: ColumnFiltersState) => void;
  /** Rows shown per page before the user expands the size. Defaults to 10. */
  pageSize?: number;
  /** Selectable page sizes. Defaults to [10, 25, 50]. */
  pageSizeOptions?: number[];
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading,
  searchPlaceholder = 'Search…',
  toolbar,
  emptyState,
  onRowClick,
  mobileCardRender,
  globalFilter: controlledFilter,
  onGlobalFilterChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  pageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [uncontrolledFilter, setUncontrolledFilter] = useState('');
  const [uncontrolledColumnFilters, setUncontrolledColumnFilters] = useState<ColumnFiltersState>([]);
  const globalFilter = controlledFilter ?? uncontrolledFilter;
  const setGlobalFilter = onGlobalFilterChange ?? setUncontrolledFilter;
  const columnFilters = controlledColumnFilters ?? uncontrolledColumnFilters;
  const setColumnFilters = onColumnFiltersChange ?? setUncontrolledColumnFilters;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, columnFilters },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: (updater) =>
      setColumnFilters(typeof updater === 'function' ? updater(columnFilters) : updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize, pageIndex: 0 } },
    globalFilterFn: 'includesString',
  });

  const rows = table.getRowModel().rows;
  const empty = !loading && rows.length === 0;
  const placeholderRows = useMemo(() => Array.from({ length: 8 }, (_, i) => i), []);

  // Pagination derived state. getFilteredRowModel reflects search/column
  // filters, so the count and range track what the user is actually viewing.
  const { pageIndex, pageSize: currentPageSize } = table.getState().pagination;
  const filteredTotal = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const firstRow = filteredTotal === 0 ? 0 : pageIndex * currentPageSize + 1;
  const lastRow = pageIndex * currentPageSize + rows.length;

  return (
    <div className="surface-card !p-0 overflow-hidden">
      {/* toolbar */}
      <div className="flex flex-col gap-2 px-4 py-3 border-b border-border md:flex-row md:items-center md:gap-3">
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-tertiary pointer-events-none" />
          <Input
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 md:ml-auto">{toolbar}</div>
      </div>

      {/* table — desktop / tablet */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-dense">
          <thead className="sticky top-0 z-10 bg-bg-surface">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-border">
                {hg.headers.map((header) => {
                  const sortable = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'text-left font-medium text-text-secondary',
                        'px-4 py-2.5 align-middle whitespace-nowrap',
                        sortable && 'cursor-pointer select-none hover:text-text-primary',
                        (header.column.columnDef.meta as { align?: string } | undefined)?.align === 'right' && 'text-right',
                      )}
                      onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {sortable && (
                          sortDir === 'asc' ? <ArrowUp className="size-3" /> :
                          sortDir === 'desc' ? <ArrowDown className="size-3" /> :
                          <ArrowUpDown className="size-3 opacity-30" />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && placeholderRows.map((i) => (
              <tr key={i} className="border-b border-border last:border-0">
                {table.getAllColumns().map((c) => (
                  <td key={c.id} className="px-4 py-2.5"><Skeleton className="h-3.5 w-3/5" /></td>
                ))}
              </tr>
            ))}

            {!loading && rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn(
                  'border-b border-border last:border-0 transition-colors',
                  'hover:bg-bg-subtle',
                  onRowClick && 'cursor-pointer',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'px-4 py-2.5 align-middle text-text-primary',
                      (cell.column.columnDef.meta as { align?: string } | undefined)?.align === 'right' && 'text-right num',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}

            {empty && (
              <tr>
                <td colSpan={table.getAllColumns().length} className="p-0">
                  {emptyState}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* card list — mobile */}
      <ul className="md:hidden divide-y divide-border">
        {loading && placeholderRows.map((i) => (
          <li key={i} className="px-4 py-3 space-y-1.5">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </li>
        ))}

        {!loading && rows.map((row) => {
          const cells = row.getVisibleCells();
          const titleCell = cells[0];
          const secondary = cells.slice(1, 4);
          const content = mobileCardRender ? mobileCardRender(row.original) : (
            <>
              <div className="flex-1 min-w-0 space-y-0.5">
                {titleCell && (
                  <div className="text-body-sm font-medium text-text-primary truncate">
                    {flexRender(titleCell.column.columnDef.cell, titleCell.getContext())}
                  </div>
                )}
                {secondary.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-caption text-text-tertiary">
                    {secondary.map((cell) => (
                      <span key={cell.id} className="truncate max-w-full">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {onRowClick && <ChevronRight className="size-4 text-text-tertiary shrink-0" />}
            </>
          );
          return (
            <li key={row.id}>
              {onRowClick ? (
                <button
                  type="button"
                  onClick={() => onRowClick(row.original)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-bg-subtle active:bg-bg-subtle"
                >
                  {content}
                </button>
              ) : (
                <div className="flex items-center gap-3 px-4 py-3">{content}</div>
              )}
            </li>
          );
        })}

        {empty && <li className="p-0">{emptyState}</li>}
      </ul>

      {/* footer */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border text-caption text-text-tertiary">
        <span className="truncate">
          {loading
            ? 'Loading…'
            : filteredTotal === 0
              ? '0 rows'
              : `Showing ${firstRow}–${lastRow} of ${filteredTotal} rows`}
        </span>

        <div className="flex items-center gap-4">
          <label className="hidden md:flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={currentPageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className={cn(
                'h-7 rounded-md border border-border bg-bg-surface px-2 text-text-secondary',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40',
              )}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-1.5">
            <span>Page {pageCount === 0 ? 0 : pageIndex + 1} of {pageCount}</span>
            <button
              type="button"
              aria-label="Previous page"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className={cn(
                'inline-flex size-7 items-center justify-center rounded-md border border-border text-text-secondary',
                'hover:bg-bg-subtle disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
              )}
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next page"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className={cn(
                'inline-flex size-7 items-center justify-center rounded-md border border-border text-text-secondary',
                'hover:bg-bg-subtle disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
              )}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
